import { OTPInput, REGEXP_ONLY_DIGITS } from "input-otp";
import { Slot } from "radix-ui";
import type { ComponentProps } from "react";
import QRCode from "react-qr-code";
import { Toaster as SonnerToaster, toast } from "sonner";

import { cn } from "../lib/cn";
import { ScrollX } from "./ScrollX";

// ── Toasts ───────────────────────────────────────────────────────────

/** Mount once near the app root. Show messages with `toast.success("Saved")`. */
export function Toaster() {
  return (
    <SonnerToaster
      position="top-right"
      richColors
      closeButton
      toastOptions={{ className: "rounded-card font-sans" }}
    />
  );
}

export { toast };

// ── Table ────────────────────────────────────────────────────────────

export interface TableProps extends ComponentProps<"table"> {
  /** Name of the side-scroll region announced when the table is wider than the screen. */
  scrollLabel?: string;
}

/** Wide tables scroll sideways inside a `ScrollX` box (arrows and drag, no scrollbar). */
export function Table({ className, scrollLabel, ...props }: TableProps) {
  return (
    <ScrollX label={scrollLabel}>
      <table className={cn("w-full caption-bottom text-sm", className)} {...props} />
    </ScrollX>
  );
}

export function TableHeader({ className, ...props }: ComponentProps<"thead">) {
  return <thead className={cn("bg-surface-muted [&_tr]:border-b", className)} {...props} />;
}

export function TableBody({ className, ...props }: ComponentProps<"tbody">) {
  return <tbody className={cn("[&_tr:last-child]:border-0", className)} {...props} />;
}

export function TableRow({ className, ...props }: ComponentProps<"tr">) {
  return (
    <tr
      className={cn(
        "border-b border-border transition-colors hover:bg-surface-muted data-[state=selected]:bg-brand-soft",
        className,
      )}
      {...props}
    />
  );
}

export function TableHead({ className, ...props }: ComponentProps<"th">) {
  return (
    <th
      className={cn(
        "h-11 px-4 text-left align-middle text-xs font-semibold tracking-wide text-muted-foreground uppercase",
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: ComponentProps<"td">) {
  return <td className={cn("px-4 py-3 align-middle", className)} {...props} />;
}

// ── OTP input (2FA codes) ────────────────────────────────────────────

export interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  onComplete?: (value: string) => void;
  "aria-label"?: string;
}

export function OtpInput({ length = 6, invalid, ...props }: OtpInputProps) {
  return (
    <OTPInput
      maxLength={length}
      pattern={REGEXP_ONLY_DIGITS}
      inputMode="numeric"
      autoComplete="one-time-code"
      containerClassName="flex items-center justify-center gap-2"
      render={({ slots }) => (
        <>
          {slots.map((slot, index) => (
            <div
              key={index}
              className={cn(
                "flex size-12 items-center justify-center rounded-input border border-input bg-surface text-xl font-semibold",
                slot.isActive && "border-foreground ring-2 ring-brand",
                invalid && "border-danger",
              )}
            >
              {slot.char ??
                (slot.hasFakeCaret ? (
                  <span className="h-6 w-px animate-pulse bg-foreground" />
                ) : null)}
            </div>
          ))}
        </>
      )}
      {...props}
    />
  );
}

// ── QR code ──────────────────────────────────────────────────────────

export interface QrCodeProps {
  value: string;
  size?: number;
  title?: string;
}

/** Black-on-white QR (scanners need that contrast, also in dark mode). */
export function QrCode({ value, size = 168, title = "QR code" }: QrCodeProps) {
  return (
    <div className="inline-block rounded-input bg-ink-foreground p-3">
      <QRCode value={value} size={size} title={title} />
    </div>
  );
}

// ── Text link ────────────────────────────────────────────────────────

export interface TextLinkProps extends ComponentProps<"a"> {
  /** Render the child (e.g. a router <Link>) with link styling. */
  asChild?: boolean;
}

export function TextLink({ asChild, className, ...props }: TextLinkProps) {
  const Component = asChild ? Slot.Root : "a";
  return (
    <Component
      className={cn("font-medium text-link underline-offset-4 hover:underline", className)}
      {...props}
    />
  );
}
